/// Read-side of the payments system. Subscribes to the user's
/// `webUsers/{uid}.purchases` map and exposes synchronous
/// `isLevelUnlocked` predicates to React components.
///
/// **Writes happen server-side only** — the Stripe webhook
/// Cloud Function (`functions/src/stripe.ts`) is the only writer.
/// Clients can read their own purchases (Firestore rule
/// `request.auth.uid == uid`) but cannot mutate the map.
///
/// Cross-device "restore purchases" is automatic: the same
/// signed-in account on a new device reads the same Firestore
/// doc and sees their unlock immediately.

import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestore } from '../firebase/app'
import { useProfileStore } from '../state/profileStore'
import type { LevelData } from '../types/LevelData'

/// Shape of `webUsers/{uid}.purchases`. Written by the Stripe
/// webhook on `checkout.session.completed`. Web port has a
/// single product (FULL_UNLOCK); presence of `full` ⟹ unlocked.
/// `purchasedAt` is millis-since-epoch, server-stamped.
export interface PurchasesMap {
  full?: { purchasedAt: number; stripeSessionId: string }
}

/// Snapshot of unlocks for a user.
export interface UnlockState {
  fullUnlock: boolean
  /// Number of unused rewarded-ad credits (parked feature — see
  /// BACKLOG). Stored on `webUsers/{uid}.adRewards.bonusAttempts`
  /// in the same Firestore doc. Always 0 today since AdMob has
  /// no web platform; kept so the field exists when ads land.
  bonusAdAttempts: number
}

const EMPTY_UNLOCKS: UnlockState = {
  fullUnlock: false,
  bonusAdAttempts: 0,
}

/// React hook — subscribes to the signed-in user's purchases doc
/// and returns the current unlock state. Re-renders whenever
/// the Stripe webhook updates the Firestore doc (real-time).
export function useUnlocks(): UnlockState {
  const profileState = useProfileStore((s) => s.state)
  const uid = profileState.kind === 'ready' ? profileState.user.uid : null
  const [unlocks, setUnlocks] = useState<UnlockState>(EMPTY_UNLOCKS)

  useEffect(() => {
    if (!uid) {
      setUnlocks(EMPTY_UNLOCKS)
      return
    }
    const ref = doc(firestore, 'webUsers', uid)
    const unsubscribe = onSnapshot(ref, (snap) => {
      const data = snap.data() ?? {}
      const purchases = (data.purchases ?? {}) as PurchasesMap
      const adRewards = (data.adRewards ?? {}) as { bonusAttempts?: number }
      setUnlocks({
        fullUnlock: !!purchases.full,
        bonusAdAttempts: adRewards.bonusAttempts ?? 0,
      })
    })
    return () => unsubscribe()
  }, [uid])

  return unlocks
}

/// True if the user can play this level given their current
/// unlocks. `isFree: true` always plays. Otherwise FULL_UNLOCK
/// must be set.
export function isLevelUnlocked(
  level: Pick<LevelData, 'isFree'>,
  unlocks: UnlockState,
): boolean {
  if (level.isFree) return true
  if (unlocks.fullUnlock) return true
  return false
}

/// True if the level requires payment AND can be unlocked for a
/// single play via a rewarded-ad watch. Used by the AdGate UI to
/// decide whether to show the "Watch ad to play" button.
/// Currently always false — rewarded ads are parked (see BACKLOG).
export function canUnlockViaAd(
  level: Pick<LevelData, 'isFree'>,
  unlocks: UnlockState,
): boolean {
  if (isLevelUnlocked(level, unlocks)) return false
  return unlocks.bonusAdAttempts > 0
}
